// ─────────────────────────────────────────────────────────────────────────────
// osStructureClient — the ONE network call the live graph makes.
//
// It reaches GET /api/os/structure and nothing else. That is the whole surface:
// no agent chat endpoint, no memory endpoint, no Dreamstate, no Pulse, no LoRA,
// no tool invocation, no model generation, and no write of any kind. If the
// network audit for a live graph mount ever shows a second request from this
// module, that is a fault, not a feature.
//
// AUTH IS THE APP'S EXISTING ONE. The session token lives in this document's
// memory (window.__rexyToken, never localStorage) and is sent as an Authorization
// Bearer exactly as App.tsx's axios interceptor does. Live structure is
// authenticated for the same reason every other real read is: the synthetic lab
// holds nothing, and the real domain/agent topology is not something to hand out
// before the PIN.
//
// API_BASE is derived here from the PAGE PROTOCOL, matching App.tsx, SecurityPage,
// ChatWindow and useRexySocket. That per-module derivation is the app's existing
// convention rather than a new duplication — keying on NODE_ENV is the mistake it
// exists to avoid, since the CRA dev server is development even behind HTTPS.
// ─────────────────────────────────────────────────────────────────────────────

import { OsStructureDto } from './RexyOsStructureGraphSource';

const API_BASE = window.location.protocol === 'https:'
  ? `https://${window.location.host}/api`
  : `${window.location.protocol}//${window.location.hostname}:5000/api`;

export const OS_STRUCTURE_PATH = '/os/structure';
// G2.4 — the cheap "did anything move?" read. The graph polls THIS on a timer
// and only fetches the full topology when the revision actually differs, so an
// idle OS costs one small JSON body per interval instead of the entity set.
export const OS_REVISION_PATH = '/os/structure/revision';

export class OsStructureAuthError extends Error {}

function authHeaders(): { [k: string]: string } {
  const token = (window as any).__rexyToken || '';
  const headers: { [k: string]: string } = { Accept: 'application/json' };
  if (token) {
    headers.Authorization = 'Bearer ' + token;
    headers['X-Rexy-Token'] = token;
  }
  return headers;
}

export interface OsRevisionDto {
  revision: string;
  refreshedAt: string | null;
  checkedAt: string | null;
  entities: number | null;
  relations: number | null;
}

/**
 * Fetch the structural revision only. Same auth, same GET-only rule, no
 * topology body — this is the call that runs on the poll timer.
 */
export async function fetchOsRevision(): Promise<OsRevisionDto> {
  const res = await fetch(API_BASE + OS_REVISION_PATH, {
    method: 'GET',
    headers: authHeaders(),
    credentials: 'omit',
  });
  if (res.status === 401 || res.status === 403) {
    throw new OsStructureAuthError('live structure requires an authenticated Rexy session');
  }
  if (!res.ok) {
    throw new Error('os revision request failed: HTTP ' + res.status);
  }
  const body = await res.json();
  if (!body || body.success !== true || typeof body.revision !== 'string') {
    throw new Error('os revision response malformed');
  }
  return body as OsRevisionDto;
}

/**
 * Fetch the structural DTO. Throws OsStructureAuthError on 401/403 so the caller
 * can say "not authenticated" rather than rendering an empty galaxy that looks
 * like an OS with no domains in it.
 */
export async function fetchOsStructure(): Promise<OsStructureDto> {
  const token = (window as any).__rexyToken || '';
  const headers: { [k: string]: string } = { Accept: 'application/json' };
  if (token) {
    headers.Authorization = 'Bearer ' + token;
    headers['X-Rexy-Token'] = token;
  }

  const res = await fetch(API_BASE + OS_STRUCTURE_PATH, {
    method: 'GET',
    headers,
    // No credentials: auth is header-only (server/app.py require_auth), and
    // asking for cookies here would quietly widen what the request carries.
    credentials: 'omit',
  });

  if (res.status === 401 || res.status === 403) {
    throw new OsStructureAuthError('live structure requires an authenticated Rexy session');
  }
  if (!res.ok) {
    throw new Error('os structure request failed: HTTP ' + res.status);
  }

  const body = await res.json();
  if (!body || body.success !== true || !body.structure) {
    throw new Error('os structure response malformed');
  }
  return body.structure as OsStructureDto;
}
